'use client';

/**
 * ProjectChat - Project-level message view and WhatsApp compose
 *
 * Shows all messages from project's RFQs and POs with WhatsApp compose.
 *
 * @component_role Project-level Chat
 * @business_rule Shows all messages across project's RFQs and POs in chronological
 *   order. Send WhatsApp from here to any contact on the project (auto-populated
 *   from RFQ/PO vendors). Messages post to Odoo chatter for audit trail.
 * @user_outcome See complete project communication history in one place. Send
 *   follow-ups without switching to WhatsApp or Odoo.
 * @integration_point klyde_edge /neo/project/messages, /messaging/whatsapp/send-to-contact
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useState } from 'react';
import { MessageSquare, RefreshCw, User } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useProjectMessages, useProjectContacts, useProjectExplore, useSendWhatsApp } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import type { MessageItem, ContactSummary, WhatsAppMediaAttachment } from '@/lib/api/types';

// Shared components
import { LoadingState, ErrorState } from './shared';
import { ContactSelector } from './contact-selector';
import { WhatsAppComposeWithContactSelector } from './whatsapp-compose';

// ============================================================
// Configuration Maps (Declarative Style)
// ============================================================

const SOURCE_CONFIG: Record<string, { color: string; label: (name?: string | null) => string }> = {
  project: {
    color: 'bg-primary/10 text-primary',
    label: () => 'Project',
  },
  rfq: {
    color: 'bg-info/10 text-info',
    label: (name) => `RFQ: ${name || 'Unknown'}`,
  },
  purchase_order: {
    color: 'bg-success/10 text-success',
    label: (name) => `PO: ${name || 'Unknown'}`,
  },
};

// ============================================================
// Sub-Components (Domain-Specific)
// ============================================================

function MessageCard({ message }: { message: MessageItem }) {
  const config = SOURCE_CONFIG[message.source] || SOURCE_CONFIG.project;

  const dateStr = message.date
    ? new Date(message.date).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <User className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">{message.author || 'Unknown'}</span>
        </div>
        <span className="text-xs text-muted-foreground">{dateStr}</span>
      </div>

      <p className="text-sm whitespace-pre-wrap">{message.body}</p>

      <div className="mt-2 flex items-center gap-2">
        <Badge variant="secondary" className={cn('text-[10px]', config.color)}>
          {config.label(message.source_name)}
        </Badge>
        {message.message_type && message.message_type !== 'comment' && (
          <Badge variant="secondary" className="text-[10px]">
            {message.message_type}
          </Badge>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

interface ProjectChatProps {
  projectId: string;
}

export function ProjectChat({ projectId }: ProjectChatProps) {
  const [selectedContact, setSelectedContact] = useState<ContactSummary | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualPhone, setManualPhone] = useState('');
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [attachments, setAttachments] = useState<WhatsAppMediaAttachment[]>([]);

  const sendWhatsAppStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'send_whatsapp'));
  const projectExploreStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'explore'));
  const messagesStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'history'));
  const projectContactsStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'project_contacts'));
  const sendWhatsApp = useSendWhatsApp(sendWhatsAppStrategy!);

  // Data fetching
  const { data: projectData } = useProjectExplore(projectExploreStrategy!, {
    project_id: projectId,
    include_relationships: [],
    max_items: 1,
  });

  const {
    data: messagesData,
    isLoading: messagesLoading,
    error: messagesError,
    refetch: refetchMessages,
  } = useProjectMessages(messagesStrategy!, {
    project_id: projectId,
    max_messages: 100,
    exclude_platforms: ['klyde_ai'],  // Hide AI synthesis messages in chat
  });

  const { data: contactsData, isLoading: contactsLoading } = useProjectContacts(projectContactsStrategy!, {
    project_id: projectId,
  });

  // Derived state
  const messages = [...(messagesData?.messages || [])].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    return dateB - dateA;
  });
  const metadata = messagesData?.metadata;
  const contacts = contactsData?.contacts || [];

  // Early returns for loading/error states
  if (messagesLoading) {
    return <LoadingState message="Loading messages..." />;
  }

  if (messagesError) {
    return <ErrorState message="Failed to load messages" detail={(messagesError as Error).message} />;
  }

  // Handlers
  const handleSendWhatsApp = async () => {
    const projectOdooId = projectData?.project?.odoo_id;
    if (!projectOdooId) {
      toast.error('Cannot send message', { description: 'Project details not loaded' });
      return;
    }

    const phoneToUse = showManualEntry ? manualPhone : selectedContact?.phone;
    if (!phoneToUse) {
      toast.error('No phone number', { description: 'Please select a contact or enter a phone number' });
      return;
    }

    sendWhatsApp.mutate(
      {
        to: phoneToUse,
        message: whatsappMessage,
        context_model: 'project.project',
        context_id: projectOdooId,
        media: attachments.length > 0 ? attachments : undefined,
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            toast.success('WhatsApp sent', { description: `Sent to ${selectedContact?.name || phoneToUse}` });
            setWhatsappMessage('');
            setAttachments([]);
            refetchMessages();
          } else {
            toast.error('Failed to send', { description: data.whatsapp_error || 'Unknown error' });
          }
        },
        onError: (err) => {
          toast.error('Send failed', { description: (err as Error).message });
        },
      }
    );
  };

  const canSend =
    !!((showManualEntry && manualPhone.trim()) || (!showManualEntry && selectedContact?.phone)) &&
    (!!whatsappMessage.trim() || attachments.length > 0) &&
    !sendWhatsApp.isPending;

  // Metadata stats as data
  const stats = metadata
    ? [
        { label: 'messages', value: metadata.message_count },
        { label: 'RFQs', value: metadata.rfq_count },
        { label: 'POs', value: metadata.po_count },
      ]
    : [];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-panel-border shrink-0">
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground">PROJECT MESSAGES</h4>
          {stats.length > 0 && (
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              {stats.map((stat, i) => (
                <span key={stat.label}>
                  {i > 0 && <span className="mr-2">•</span>}
                  {stat.value} {stat.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => refetchMessages()}
          disabled={messagesLoading}
          title="Refresh messages"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', messagesLoading && 'animate-spin')} />
        </Button>
      </div>

      {/* Messages list */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-3 space-y-3 pb-4">
          {messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No messages yet</p>
            </div>
          ) : (
            messages.map((msg, idx) => <MessageCard key={idx} message={msg} />)
          )}
        </div>
      </div>

      {/* WhatsApp Compose */}
      <WhatsAppComposeWithContactSelector
        message={whatsappMessage}
        onMessageChange={setWhatsappMessage}
        onSend={handleSendWhatsApp}
        canSend={canSend}
        isSending={sendWhatsApp.isPending}
        selectedContactName={selectedContact?.name}
        showManualEntry={showManualEntry}
        manualPhone={manualPhone}
        onManualPhoneChange={setManualPhone}
        onCancelManualEntry={() => {
          setShowManualEntry(false);
          setManualPhone('');
        }}
        warning={selectedContact && !selectedContact.phone ? 'This contact has no phone number' : undefined}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
      >
        <ContactSelector
          contacts={contacts}
          selectedContact={selectedContact}
          onSelect={setSelectedContact}
          onManualEntry={() => setShowManualEntry(true)}
          isLoading={contactsLoading}
        />
      </WhatsAppComposeWithContactSelector>
    </div>
  );
}
